/**
 * 관리자 프롬프트 템플릿 (M14 / Phase 1) API wrapper.
 *
 * 백엔드 엔드포인트:
 *   GET    /api/admin/prompts?name=&status=
 *   GET    /api/admin/prompts/active?name=invoice_extractor_v1
 *   GET    /api/admin/prompts/history/{name}
 *   GET    /api/admin/prompts/{id}
 *   POST   /api/admin/prompts
 *   PATCH  /api/admin/prompts/{id}
 *   DELETE /api/admin/prompts/{id}
 *   POST   /api/admin/prompts/{id}/activate
 *   POST   /api/admin/prompts/{id}/rollback
 *   POST   /api/admin/prompts/test
 *
 * 모든 엔드포인트는 ``require_admin`` 가드.
 */

import { apiClient } from "./client";

export type PromptStatus = "draft" | "active" | "archived";

/**
 * user_prompt_template 에 반드시 포함되어야 하는 placeholder 토큰들.
 * 백엔드 ``app/models/prompt_template.py`` 의 ``REQUIRED_PLACEHOLDERS`` 와 동기 유지.
 *
 * 누락 시 PATCH/POST 가 422 로 거부되며, 프런트 에디터는 라이브로 검증해 미리 안내.
 */
export const REQUIRED_PROMPT_PLACEHOLDERS = [
  "{currency_instruction}",
  "{swift_instruction}",
  "{custom_rules_section}",
] as const;

export const DEFAULT_PROMPT_NAME = "invoice_extractor_v1" as const;

export interface PromptTemplateOut {
  id: number;
  name: string;
  system_prompt: string;
  user_prompt_template: string;
  status: PromptStatus;
  version: number;
  notes: string | null;
  created_by_user_id: number | null;
  parent_id: number | null;
  created_at: string;
  updated_at: string;
  activated_at: string | null;
}

export interface PromptTemplateCreatePayload {
  name?: string;
  system_prompt: string;
  user_prompt_template: string;
  notes?: string | null;
  /** 클론 표시용 (옵션). 본문 자체는 payload 의 값이 그대로 들어간다. */
  parent_id?: number | null;
}

export interface PromptTemplateUpdatePayload {
  system_prompt?: string;
  user_prompt_template?: string;
  notes?: string | null;
}

export interface PromptTemplateActivatePayload {
  notes?: string | null;
}

// ────────────── 리스트 / 조회 ──────────────

export interface ListPromptsParams {
  name?: string;
  status?: PromptStatus;
}

export async function listPrompts(params: ListPromptsParams = {}): Promise<PromptTemplateOut[]> {
  const query: Record<string, string> = {};
  if (params.name) query.name = params.name;
  if (params.status) query.status = params.status;
  const res = await apiClient.get<PromptTemplateOut[]>("/admin/prompts", { params: query });
  return res.data;
}

/**
 * 현재 active 단일 행 조회. 없으면 백엔드가 404 + ``"코드 default 사용 중"`` 메시지.
 * 호출측은 ``404`` 를 정상 케이스로 다뤄야 한다.
 */
export async function getActivePrompt(
  name: string = DEFAULT_PROMPT_NAME,
): Promise<PromptTemplateOut> {
  const res = await apiClient.get<PromptTemplateOut>("/admin/prompts/active", {
    params: { name },
  });
  return res.data;
}

export interface PromptDefaults {
  system_prompt: string;
  user_prompt_template: string;
}

/** 코드 기본 프롬프트 본문 — 에디터 '기본 템플릿 불러오기' 시작점. */
export async function getPromptDefaults(): Promise<PromptDefaults> {
  const res = await apiClient.get<PromptDefaults>("/admin/prompts/defaults");
  return res.data;
}

export async function getPromptHistory(name: string): Promise<PromptTemplateOut[]> {
  const res = await apiClient.get<PromptTemplateOut[]>(
    `/admin/prompts/history/${encodeURIComponent(name)}`,
  );
  return res.data;
}

export async function getPrompt(id: number): Promise<PromptTemplateOut> {
  const res = await apiClient.get<PromptTemplateOut>(`/admin/prompts/${id}`);
  return res.data;
}

// ────────────── 변경 ──────────────

export async function createPromptDraft(
  payload: PromptTemplateCreatePayload,
): Promise<PromptTemplateOut> {
  const res = await apiClient.post<PromptTemplateOut>("/admin/prompts", payload);
  return res.data;
}

export async function updatePromptDraft(
  id: number,
  payload: PromptTemplateUpdatePayload,
): Promise<PromptTemplateOut> {
  const res = await apiClient.patch<PromptTemplateOut>(`/admin/prompts/${id}`, payload);
  return res.data;
}

export async function deletePromptDraft(id: number): Promise<void> {
  await apiClient.delete(`/admin/prompts/${id}`);
}

/**
 * draft → active.  같은 ``name`` 의 이전 active 가 있으면 자동으로 archived.
 */
export async function activatePrompt(
  id: number,
  payload: PromptTemplateActivatePayload = {},
): Promise<PromptTemplateOut> {
  const res = await apiClient.post<PromptTemplateOut>(`/admin/prompts/${id}/activate`, payload);
  return res.data;
}

/**
 * archived 행을 기반으로 새 draft 자동 생성 + 즉시 active.
 * 결과 행은 새 ``id`` / 새 ``version`` 으로 반환된다.
 */
export async function rollbackPrompt(
  archivedId: number,
  payload: PromptTemplateActivatePayload = {},
): Promise<PromptTemplateOut> {
  const res = await apiClient.post<PromptTemplateOut>(
    `/admin/prompts/${archivedId}/rollback`,
    payload,
  );
  return res.data;
}

// ────────────── Dry-run (Test) ──────────────

/**
 * Dry-run 요청. 정확히 **하나의 모드만** 채워야 한다 — 그렇지 않으면 백엔드가 400.
 *
 *   1) ``template_id`` — DB 저장 본문 사용
 *   2) ``system_prompt`` + ``user_prompt_template`` — 미저장 본문 사용
 *
 * ``call_llm=true`` 일 때만 실제 모델 호출 (비용 발생).
 */
export interface PromptTemplateTestRequest {
  ocr_text: string;
  agent_id?: string | null;
  template_id?: number | null;
  system_prompt?: string | null;
  user_prompt_template?: string | null;
  call_llm?: boolean;
}

export interface PromptTemplateTestAgentMeta {
  id: string;
  official_name: string;
  currency: string | null;
  withholding_enabled: boolean;
  has_extraction_rules: boolean;
}

export interface PromptTemplateTestResponse {
  rendered_system_prompt: string;
  rendered_user_prompt: string;
  agent_used: PromptTemplateTestAgentMeta | null;
  extracted: Record<string, unknown> | null;
}

export async function testPrompt(
  payload: PromptTemplateTestRequest,
): Promise<PromptTemplateTestResponse> {
  const res = await apiClient.post<PromptTemplateTestResponse>("/admin/prompts/test", payload);
  return res.data;
}

// ────────────── 클라이언트 사이드 유틸 ──────────────

/**
 * 본문에서 누락된 필수 placeholder 토큰을 반환 (라이브 검증용).
 * 빈 배열이면 백엔드의 ``_check_placeholders`` 와 동등하게 통과한다.
 */
export function findMissingPlaceholders(body: string): string[] {
  return REQUIRED_PROMPT_PLACEHOLDERS.filter((p) => !body.includes(p));
}
